import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { setCookieConsent, initializeAnalytics, initializeMarketing, initializeFunctional } from '../utils/cookieConsent';

const CookieConsentModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show modal after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    setCookieConsent(allAccepted);
    setIsVisible(false);
    
    // Initialize all tracking and features
    initializeAnalytics();
    initializeMarketing();
    initializeFunctional();
  };

  const handleAcceptSelected = () => {
    setCookieConsent(preferences);
    setIsVisible(false);
    
    // Initialize features based on selected preferences
    if (preferences.analytics) initializeAnalytics();
    if (preferences.marketing) initializeMarketing();
    if (preferences.functional) initializeFunctional();
  };

  const handleRejectAll = () => {
    const rejected = {
      essential: true, // Essential cookies cannot be rejected
      analytics: false,
      functional: false,
      marketing: false
    };
    setCookieConsent(rejected);
    setIsVisible(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-sm">
      <div className="bg-background border-t border-border shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {!showDetails ? (
            // Simple view
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Icon name="Shield" size={16} className="text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    We value your privacy
                  </h3>
                </div>
                <p className="text-text-secondary text-sm">
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. 
                  <Link to="/cookie-policy" className="text-secondary hover:underline ml-1">
                    Learn more
                  </Link>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(true)}
                  className="whitespace-nowrap"
                >
                  Customize
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRejectAll}
                  className="whitespace-nowrap"
                >
                  Reject All
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAcceptAll}
                  className="whitespace-nowrap"
                >
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            // Detailed view
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Icon name="Settings" size={16} className="text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    Cookie Preferences
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails(false)}
                  iconName="X"
                />
              </div>

              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon name="Shield" size={16} className="text-secondary" />
                      <h4 className="font-medium text-text-primary">Essential Cookies</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      These cookies are necessary for the website to function properly. They cannot be disabled.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                    />
                    <span className="text-sm text-text-secondary">Always Active</span>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon name="BarChart3" size={16} className="text-secondary" />
                      <h4 className="font-medium text-text-primary">Analytics Cookies</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                      className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                    />
                    <span className="text-sm text-text-secondary">Optional</span>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon name="Settings" size={16} className="text-secondary" />
                      <h4 className="font-medium text-text-primary">Functional Cookies</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Enable enhanced functionality and personalization, such as remembering your preferences and settings.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => handlePreferenceChange('functional')}
                      className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                    />
                    <span className="text-sm text-text-secondary">Optional</span>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon name="Target" size={16} className="text-secondary" />
                      <h4 className="font-medium text-text-primary">Marketing Cookies</h4>
                    </div>
                    <p className="text-sm text-text-secondary">
                      Used to track visitors across websites to display relevant and engaging advertisements.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                      className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                    />
                    <span className="text-sm text-text-secondary">Optional</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Link to="/cookie-policy">
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                  >
                    Cookie Policy
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRejectAll}
                  className="whitespace-nowrap"
                >
                  Reject All
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAcceptSelected}
                  className="whitespace-nowrap"
                >
                  Accept Selected
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentModal;
